import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Location } from "@/types/location";
import { LocationsTable } from "@/components/admin/locations/LocationsTable";
import { EditLocationDialog } from "@/components/admin/locations/EditLocationDialog";
import { NewLocationDialog } from "@/components/admin/locations/NewLocationDialog";

export default function Locations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
    if (!isAuthenticated) {
      navigate("/auth");
    }
    fetchLocations();
  }, [navigate]);

  const fetchLocations = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('cat_locations').select('*').order('name');
      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Erro ao carregar localizações:', error);
      toast.error('Erro ao carregar localizações');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingLocation) return;
    try {
      const {
        error
      } = await supabase.from('cat_locations').update({
        name: editingLocation.name,
        description: editingLocation.description,
        active: editingLocation.active
      }).eq('id', editingLocation.id);
      if (error) throw error;
      toast.success('Localização atualizada com sucesso');
      setEditingLocation(null);
      fetchLocations();
    } catch (error) {
      console.error('Erro ao atualizar localização:', error);
      toast.error('Erro ao atualizar localização');
    }
  };

  const handleCreate = async (newLocation: Omit<Location, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const {
        error
      } = await supabase.from('cat_locations').insert([{
        name: newLocation.name,
        description: newLocation.description,
        active: true
      }]);
      if (error) throw error;
      toast.success('Localização criada com sucesso');
      setIsNewDialogOpen(false);
      fetchLocations();
    } catch (error) {
      console.error('Erro ao criar localização:', error);
      toast.error('Erro ao criar localização');
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 ml-[250px]">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Localizações</h1>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')} 
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Button 
                onClick={() => setIsNewDialogOpen(true)} 
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600"
              >
                <PlusCircle className="w-4 h-4" />
                Nova Localização
              </Button>
            </div>
          </div>

          <div className="max-w-[1200px]">
            {isLoading ? (
              <div>Carregando...</div>
            ) : (
              <LocationsTable 
                locations={locations} 
                onEdit={setEditingLocation} 
              />
            )}
          </div>
        </div>

        <EditLocationDialog 
          location={editingLocation} 
          onClose={() => setEditingLocation(null)} 
          onSave={handleEdit} 
          onChange={setEditingLocation} 
        />

        <NewLocationDialog 
          open={isNewDialogOpen} 
          onClose={() => setIsNewDialogOpen(false)} 
          onSave={handleCreate} 
        />
      </div>
    </div>
  );
}
